import {FC, useRef, useMemo, MutableRefObject} from "react";
import { useReactToPrint } from "react-to-print";
import {
  EnvelopeIcon,
  GlobeAltIcon,
  PhoneIcon,
} from "@heroicons/react/20/solid";
import { MapPinIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import lodash from "lodash";
import { useUrlState } from "utils";
import GenerateMeterDeliveryMaterialsContainer from "./cards/generate-materials";

interface FATInvoiceDetailsContainerProps {
  deliveryRequest: any;
  id: string;
  code: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const FATDispatchCertificateDetailsContainer: FC<
  FATInvoiceDetailsContainerProps
> = ({ deliveryRequest, code }) => {

  const [modal, setModal] = useUrlState("modal");

  const unifiedData = useMemo(() => {

    const includesMMSCompliant =  deliveryRequest?.response?.dispatchDetails?.some((details: any) => details.isMMSCompliant);
    const includesNonMMSCompliant =  deliveryRequest?.response?.dispatchDetails?.some((details: any) => !details.isMMSCompliant);

    let singlePhaseMMSCompliant = 0
    let singlePhaseNonMMSCompliant = 0
    let threePhaseMMSCompliant = 0
    let threePhaseNonMMSCompliant = 0
    let districtClearances: { [k in string]: { districts: { name: string, regionName: string }[], setCount: number, phase: string, mmsCompliant: boolean } }  = {}

    for(let i = 0; i < deliveryRequest?.response?.dispatchDetails?.length; i++){
      let details = deliveryRequest?.response?.dispatchDetails[i];

      // Calculate compliance numbers

      if(details.meterPhase === "1 Phase"){
        if(details.isMMSCompliant){
          singlePhaseMMSCompliant += (details.setCount || 0);
        } else {
          singlePhaseNonMMSCompliant += (details.setCount || 0);
        }
      } else {
        if(details.isMMSCompliant){
          threePhaseMMSCompliant += (details.setCount || 0);
        } else {
          threePhaseNonMMSCompliant += (details.setCount || 0);
        }
      }


      // Merge sets permitted in a give set of districts

      const districtsKey = details.permittedDistricts.map((district: any) => district._id)?.join("") + details.meterPhase + (details.isMMSCompliant ? "_compliant": "_noncompliant");

      const districts = details?.permittedDistricts?.map((district: any) =>({
        name: district.name,
        regionName: district.regionName
      }))

      if(lodash.has(districtClearances, districtsKey)){
        districtClearances[districtsKey].setCount += details.setCount;
      } else {

        // If all of our districts are absorbed in another set, we check the phase and add to set count

        let foundSimilarKey = false;
        Object.keys(districtClearances)?.forEach((key) => {

          if(foundSimilarKey) return

          const keyWithoutPhase = districtsKey.replace(details.meterPhase, "").replace("_compliant", "").replace("_noncompliant", "");

          if(key.includes(keyWithoutPhase)){

            if(districtClearances[key]?.phase === details.meterPhase){
              districtClearances[key].setCount += details.setCount;
              foundSimilarKey = true
              return
            }
          }
        })

        if(foundSimilarKey){
          continue
        }

        // Otherwise create your own entry
        districtClearances[districtsKey] = {
          districts,
          mmsCompliant: details.isMMSCompliant,
          setCount: details.setCount,
          phase: details.meterPhase
        }
      }
    }

    return {
      singlePhaseMMSCompliant,
      singlePhaseNonMMSCompliant,
      threePhaseMMSCompliant,
      threePhaseNonMMSCompliant,
      includesMMSCompliant,
      includesNonMMSCompliant,
      districtClearances,
    }

  }, [deliveryRequest])

  const enclosurePrintRef = useRef<any>();
  const installationsPrintRef = useRef<any>();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handlePrint = (ref: MutableRefObject<any>) =>  useReactToPrint({
    content: () => ref.current,
    documentTitle: code,
    bodyClass: "w-[1000px]",
  });


  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* RAW INFO */}
      <div className="space-y-6 print:hidden block divide-y divide-gray-200 p-6 overflow-y-auto flex flex-col flex-1">
        <div className="p-4">
          <div className="flex justify-between items-center cursor-pointer">
            <div>
              <h3 className="text-md leading-6 font-normal text-gray-900 dark:text-gray-100">
                Dispatch Clearance Certificate Information
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                Details about the clearance certificate
              </p>
            </div>
            <div />
          </div>
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-3 space-y-6 divide-y divide-gray-200 mt-4">
            <div className='pt-4 first:pt-0'>
              <span className="text-xs font-light">Meter Enclosure Information</span>
              <div className='grid grid-cols-3 gap-6 mt-2'>
                <div className="col-span-1">
                    <span className="block text-sm font-light text-gray-700">
                      Single Phase Meter Enclosures
                    </span>
                  <div className="mt-1 block w-full sm:text-sm">
                    {deliveryRequest?.request?.singlePhaseMeterEnclosures || 0}
                  </div>
                </div>
                <div className="col-span-1">
                    <span className="block text-sm font-light text-gray-700">
                      Three Phase Meter Enclosures
                    </span>
                  <div className="mt-1 block w-full sm:text-sm">
                    {deliveryRequest?.request?.threePhaseMeterEnclosures || 0}
                  </div>
                </div>
              </div>
            </div>
            {
              deliveryRequest?.response?.dispatchDetails?.map((dispatchDetails: any) => (
                <div className='pt-4 first:pt-0'>
                  <span className="text-xs font-light">Order #{dispatchDetails?.FATServiceOrder?.code} Information</span>
                  <div className='grid grid-cols-4 gap-6 mt-2'>
                    <div className="col-span-1">
                      <span className="block text-sm font-light text-gray-700">
                        Meter Brand/Model
                      </span>
                      <div className="mt-1 block w-full sm:text-sm">
                        {dispatchDetails?.meterBrand?.name || "Unknown Brand"}{" "}{dispatchDetails?.meterModel?.name || "N/A"}
                      </div>
                    </div>
                    <div className="col-span-1">
                      <span className="block text-sm font-light text-gray-700">
                        Meter Phase
                      </span>
                      <div className="mt-1 block w-full sm:text-sm">
                        {dispatchDetails?.meterPhase || "N/A"}
                      </div>
                    </div>
                    <div className="col-span-1">
                      <span className="block text-sm font-light text-gray-700">
                        Is MMS Compliant
                      </span>
                      <div className="mt-1 block w-full sm:text-sm">
                        {Boolean(dispatchDetails?.isMMSCompliant)
                          ? "Yes"
                          : "No"
                        }
                      </div>
                    </div>
                    <div className="col-span-1">
                      <span className="block text-sm font-light text-gray-700">
                        No. of Meters
                      </span>
                      <div className="mt-1 block w-full sm:text-sm">
                        {dispatchDetails?.setCount || "N/A"}
                      </div>
                    </div>
                    <div className="col-span-4">
                      <span className="block text-sm font-light text-gray-700">
                        Permitted Districts
                      </span>
                      <div className="mt-1 block w-full sm:text-sm">
                        {dispatchDetails?.permittedDistricts?.map((district: any) => district.name).join(", ") || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      {/* Meter Installation Certificate */}
      <div ref={installationsPrintRef} className="flex-1 hidden print:flex flex-col overflow-hidden">
        <ECGLetterHeader />
        <ECGLetterAddress deliveryRequest={deliveryRequest} />
        <div className={"p-12 pt-0 hidden print:flex flex-col"}>
          <div className={"text-md pt-8"}>
            <p>Dear Sir/Madam,</p>
            <p className={"uppercase text-center font-semibold underline pt-4"}>
              SUPPLY AND INSTALLATION OF{" "}
              {unifiedData?.includesMMSCompliant
                ? unifiedData?.includesNonMMSCompliant
                  ? "MMS COMPLIANT AND NON-MMS COMPLIANT METERS"
                  : "MMS COMPLIANT METERS"
                : "METERS"
              }{" "}
              BY {deliveryRequest?.meterContractor?.name}
            </p>
            <p className={"italic font-semibold pt-4"}>Dispatch Clearance</p>
          </div>
          <div className={"text-md pt-4"}>
            <p>
              We have reviewed the Factory Acceptance Test (FAT) of{" "}
              {Boolean(unifiedData.singlePhaseMMSCompliant || unifiedData.threePhaseMMSCompliant)
                ? unifiedData.singlePhaseMMSCompliant
                  ? unifiedData.threePhaseMMSCompliant
                    ? `${unifiedData.singlePhaseMMSCompliant} single-phase and ${unifiedData.threePhaseMMSCompliant} three-phase MMS-Compliant Prepayment Meters`
                    : `${unifiedData.singlePhaseMMSCompliant} single-phase MMS-Compliant Prepayment Meters`
                  : `${unifiedData.threePhaseMMSCompliant} three-phase MMS-Compliant Prepayment Meters`
                : ""
              }
              { Boolean(unifiedData.singlePhaseMMSCompliant || unifiedData.threePhaseMMSCompliant) &&
                Boolean(unifiedData.singlePhaseNonMMSCompliant || unifiedData.threePhaseNonMMSCompliant) && ", with"
              }{" "}
              {Boolean(unifiedData.singlePhaseNonMMSCompliant || unifiedData.threePhaseNonMMSCompliant)
                ? unifiedData.singlePhaseNonMMSCompliant
                  ? unifiedData.threePhaseNonMMSCompliant
                    ? `${unifiedData.singlePhaseNonMMSCompliant} single-phase and ${unifiedData.threePhaseNonMMSCompliant} three-phase Prepayment Meters.`
                    : `${unifiedData.singlePhaseNonMMSCompliant} single-phase Meters.`
                  : `${unifiedData.threePhaseNonMMSCompliant} three-phase Meters.`
                : ""
              }
            </p>
            <p className={"pt-4"}>
              Accordingly, we issue this dispatch clearance for you to supply and install:
              <br />
              {
                lodash.values(unifiedData.districtClearances)
                  .map((clearance) => {

                    function groupByRegions(districts: typeof clearance.districts){
                      let regionMap: { [k in string] : string[] } = {};
                      districts.forEach((district) => {
                        if(lodash.has(regionMap, district.regionName)){
                          regionMap[district.regionName].push(district.name)
                        } else {
                          regionMap[district.regionName] = [district.name]
                        }
                      })
                      return regionMap
                    }

                    function renderRegionGroups(region: string, districts: string[] ){
                      return districts.length > 1
                        ? districts.slice(0, -1).join(", ") + " and " + districts.at(-1) + "of the " + region
                        : `${districts[0]} of the ${region}`
                    }

                    let districtStrings = Object.entries(groupByRegions(clearance.districts)).map((args) => renderRegionGroups(args[0], args[1]))
                    let districtString = districtStrings.length > 1
                      ? districtStrings.slice(0, -1).join(", ") + " and " + districtStrings.at(-1)
                      : `${districtStrings[0]}`

                    return (
                      <>
                        {clearance.setCount} {clearance.phase === "1 Phase" ? "single-phase" : "three-phase"} {clearance.mmsCompliant ? "MMS-Compliant Prepayment": ""} Meters in the {districtString}.
                      <br />
                      </>
                    )
                  })
              }
            </p>
            <p className={"pt-4"}>
              Yours faithfully,
            </p>
          </div>
          <ECGLetterSalutations />
        </div>
      </div>

      {/* Meter Enclosure Certificate */}
      <div ref={enclosurePrintRef} className="flex-1 hidden print:flex flex-col overflow-hidden">
        {/* ECG HEADER */}
        <ECGLetterHeader />

        <ECGLetterAddress deliveryRequest={deliveryRequest} />
        {/* CERTIFICATE */}
        <div className={"p-12 pt-0 hidden print:flex flex-col"}>

            <div className={"text-md pt-8"}>
              <p>Dear Sir/Madam,</p>
              <p className={"uppercase text-center font-semibold underline pt-4"}>
                SUPPLY OF METER ENCLOSURES BY {deliveryRequest?.meterContractor?.name}
              </p>
              <p className={"italic font-semibold pt-4"}>Dispatch Clearance</p>
            </div>
            <div className={"text-md pt-4"}>
              <p>
                We have reviewed the Factory Acceptance Test (FAT) of{" "}
                {deliveryRequest?.request?.singlePhaseMeterEnclosures
                  ? deliveryRequest?.request?.threePhaseMeterEnclosures
                    ? `${deliveryRequest?.request?.singlePhaseMeterEnclosures} single-phase meter enclosures and ${deliveryRequest?.request?.threePhaseMeterEnclosures} three-phase meter enclosures`
                    : `${deliveryRequest?.request?.singlePhaseMeterEnclosures} single-phase meter enclosures`
                  : deliveryRequest?.request?.threePhaseMeterEnclosures
                    ? `${deliveryRequest?.request?.threePhaseMeterEnclosures} three-phase meter enclosures`
                    : ``
                }{" "}submitted by MTS, indicating that the
                enclosures, supplied by {deliveryRequest?.meterContractor?.name}, complied with ECG's requirements.
              </p>
              <p className={"pt-4"}>
                Accordingly, we issue this dispatch clearance for you to supply these meter enclosures.
              </p>
              <p className={"pt-4"}>
                Yours faithfully,
              </p>
            </div>
          <ECGLetterSalutations />
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 sm:py-4 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
        <button
          type="button"
          onClick={() => setModal("generate-materials")}
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
        >
          Submit Materials Requisition
        </button>
        <button
          type="button"
          onClick={handlePrint(enclosurePrintRef)}
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
        >
          Print Meter Enclosure Clearance
        </button>
        <button
          type="button"
          onClick={handlePrint(installationsPrintRef)}
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
        >
          Print Meter Installation Clearance
        </button>
      </div>

      {
        Boolean(modal) && (
          <>
            <GenerateMeterDeliveryMaterialsContainer
              open={modal === 'generate-materials'}
              setOpen={(val) => setModal(val ? 'generate-materials' : undefined)}
            />
          </>
        )
      }
    </div>
  );
};


export const ECGLetterHeader = () => (
  <div className="hidden print:flex flex-row items-center space-x-4 justify-between px-8 py-6 border-b border-gray-300">
    <div className={"flex items-center gap-4"}>
      <div>
        <img
          alt="ECG Logo"
          src={"/logo.png"}
          className="h-28 w-28"
        />
      </div>
      <div className={"flex flex-col"}>
        <h1 className="text-2xl text-ecg-primary-700 font-bold">
          ELECTRICITY COMPANY OF GHANA LIMITED
        </h1>
        <h2 className="text-lg w-fit text-ecg-primary-600 font-bold border-b pt-1 border-ecg-primary-700 ">
          HEAD OFFICE - ELECTRO-VOLTA HOUSE
        </h2>
        <h2 className="text-lg text-ecg-primary-600 font-bold">
          OFFICE OF THE PROJECT MANAGER - LRP PROJECT
        </h2>
      </div>
    </div>
    <div className="space-y-1 text-sm text-ecg-primary-700">
      <div className="flex flex-col items-end space-y-2">
        <div className="flex items-center space-x-2">
          <span>P.O BOX GP 521, ACCRA</span>
          <EnvelopeIcon className="h-4 w-4 text-red-500" />
        </div>
        <div className="flex items-center space-x-2">
          <span>+233 27 748 4947</span>
          <PhoneIcon className="h-4 w-4 text-red-500" />
        </div>
        <div className="flex items-center space-x-2">
          <span>bpi-bansa@ecggh.com</span>
          <GlobeAltIcon className="h-4 w-4 text-red-500" />
        </div>
        <div className="flex items-center space-x-2">
          <span>GA-145-7445</span>
          <MapPinIcon className="h-4 w-4 text-red-500" />
        </div>
      </div>
    </div>
  </div>
)

function ECGLetterAddress({ deliveryRequest }: { deliveryRequest: any }) {
  return (
    <div className={"p-8 pb-0"}>
      <div className={"text-md font-semibold text-ecg-primary-700 "}>
        Delivery Request: {deliveryRequest?.code}
      </div>
      <div className={"p-4"}>
        <div className={"text-md pt-6"}>
          <p>{moment(deliveryRequest?.dispatchGeneratedAt).format("Do MMMM, YYYY")}</p>
        </div>
        <div className={"text-md pt-4"}>
          <p>The Managing Director</p>
          <p>{deliveryRequest?.meterContractor?.name}</p>
          <p>
            {deliveryRequest?.meterContractor?.address}
          </p>
          <p>{deliveryRequest?.meterContractor?.ghanaPostAddress}</p>
        </div>
      </div>
    </div>
  )
}

const ECGLetterSalutations = () => (
  <div className={"text-md pt-12"}>
    <p className={"font-semibold"}>
      Benard Pi-Bansa
    </p>
    <p className={"font-semibold pt-1"}>
      Project Manager
    </p>
    <p className={"pt-4"}>
      cc: Ag. Managing Director
    </p>
    <p className={"pt-2 pl-6"}>
      DMD/Engineering & Operations <br />
      D/Customer Services <br />
      D/Materials & Transport <br />
      D/Premises & Estates <br />
      D/Finance <br />
      Contractors' Consultant
    </p>
  </div>
)

export default FATDispatchCertificateDetailsContainer;

